import { type Button, type ButtonKind, Spinner, Styles } from "@mixitone/components";
import { omit } from "@mixitone/util";
import { Link as RouterLink } from "router";
import { twMerge } from "tailwind-merge";

type RouterLinkProps = React.ComponentProps<typeof RouterLink>;

type LinkProps = RouterLinkProps & {
  children: React.ReactNode;
  kind?: ButtonKind;
  spinner?: boolean;
  className?: string;
  groupPosition?: React.ComponentProps<typeof Button>["groupPosition"];
  ref?: React.Ref<HTMLAnchorElement>;
};

/**
 * A specialized link component that combines routing functionality with button styling.
 * Wraps the router's Link component and adds support for button appearance and loading states.
 *
 * @example
 * ```tsx
 * // Basic usage with default primary style
 * <RoutedLink to="/path">Click me</RoutedLink>
 *
 * // With custom button style
 * <RoutedLink to="/path" kind="plain">Plain link</RoutedLink>
 *
 * // With loading spinner
 * <RoutedLink to="/path" spinner={true}>Loading...</RoutedLink>
 * ```
 */
const RoutedLink: React.FC<LinkProps> = ({ ref, ...props }) => {
  const classes = Styles.Buttons.buttonClasses({
    kind: props.kind || "primary",
    className: twMerge(props.className, ""),
  });

  return (
    <RouterLink
      {...omit(props, ["kind", "spinner", "groupPosition"])}
      onClick={props.onClick}
      className={classes}
      ref={ref}
    >
      <span className="inline-flex items-center gap-1">
        {props.spinner && <Spinner size={16} />}
        {!props.spinner && props.children}
      </span>
    </RouterLink>
  );
};

export { RoutedLink };
